import React, { useState } from "react";
import PropTypes from "prop-types";
import { Select, MenuItem } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useAccent, ranuTextButtonSx, ranuOutlinedButtonSx } from "./SectionCard";

// Ranu maroon, same color for every button in this row
const MAROON = "#7A1410";

// Same look as "Back to search" (text button), just a bit smaller
const smallTextButtonSx = (theme) => ({
  ...ranuTextButtonSx(theme),
  color: MAROON,
  fontSize: "0.75rem",
  "&:focus:not(:hover), &:active:not(:hover)": {
    color: MAROON,
    boxShadow: "none",
  },
});

// Add New Address / Back: same size and color, with a full 2px maroon border
const smallOutlinedButtonSx = (theme) => ({
  ...ranuOutlinedButtonSx(theme),
  color: MAROON,
  borderColor: MAROON,
  borderWidth: "2px",
  borderStyle: "solid",
  fontSize: "0.75rem",
  "&:focus:not(:hover), &:active:not(:hover)": {
    color: MAROON,
    borderColor: MAROON,
    borderWidth: "2px",
    boxShadow: "none",
  },
  "&:hover": {
    ...ranuOutlinedButtonSx(theme)["&:hover"],
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    borderWidth: "2px",
  },
});

const AddressSearch = ({
  addressList,
  selectedAddress,
  onAddressSelect,
  isNewAddress,
  onNewAddressToggle,
  onSkip,
  isAddressDone,
  isSkipped,
}) => {
  const { accentName } = useAccent();
  const [skipped, setSkipped] = useState(false);
  const hasSavedAddresses = addressList.length > 0;

  const handleSkip = () => {
    setSkipped(true);
    if (onSkip) onSkip();
  };

  // Skipped: hide everything (the parent shows the skipped message)
  if (isSkipped || skipped) return null;

  const showLabel = !isNewAddress && hasSavedAddresses;

  return (
    <MDBox mb={2}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "center", sm: showLabel ? "space-between" : "flex-end" }}
        flexWrap="wrap"
        gap={2}
        mb={1}
      >
        {/* The plain "Address" label is not needed; only "Select address" is shown */}
        {showLabel && (
          <MDTypography variant="button" fontWeight="medium" color={accentName}>
            Select address
          </MDTypography>
        )}

        <MDBox display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" gap={2}>
          {/* Add New Address  <->  Back (to the saved addresses) */}
          <MDButton
            key={isNewAddress ? "back" : "add-new"}
            type="button"
            size="small"
            variant="outlined"
            color={accentName}
            onClick={onNewAddressToggle}
            sx={smallOutlinedButtonSx}
          >
            {isNewAddress ? "Back" : "Add New Address"}
          </MDButton>

          {/* Skip is always last */}
          {onSkip && !isAddressDone && (
            <MDButton
              type="button"
              size="small"
              variant="text"
              color={accentName}
              onClick={handleSkip}
              sx={smallTextButtonSx}
            >
              Skip
            </MDButton>
          )}
        </MDBox>
      </MDBox>

      {!isNewAddress && hasSavedAddresses && (
        <div style={{ width: "100%", height: "50px", position: "relative" }}>
          <Select
            style={{ width: "100%", height: "100%" }}
            value={selectedAddress || ""}
            onChange={(e) => onAddressSelect(e.target.value)}
            required
          >
            {addressList.map((address) => (
              <MenuItem key={address.devotee_address_id} value={address.devotee_address_id}>
                {address.title} ➡ {address.address_line_1}, {address.city}
              </MenuItem>
            ))}
          </Select>
          <ArrowDropDownIcon
            fontSize="large"
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {!isNewAddress && !hasSavedAddresses && (
        <MDTypography variant="button" color="text">
          No saved address yet. Click Add New Address to add one.
        </MDTypography>
      )}
    </MDBox>
  );
};

AddressSearch.propTypes = {
  addressList: PropTypes.arrayOf(
    PropTypes.shape({
      devotee_address_id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      address_line_1: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      country: PropTypes.string,
      pincode: PropTypes.string.isRequired,
      is_default: PropTypes.oneOf([0, 1]).isRequired,
    })
  ).isRequired,
  selectedAddress: PropTypes.number,
  onAddressSelect: PropTypes.func.isRequired,
  isNewAddress: PropTypes.bool.isRequired,
  onNewAddressToggle: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
  isAddressDone: PropTypes.bool,
  isSkipped: PropTypes.bool,
};

AddressSearch.defaultProps = {
  selectedAddress: null,
  onSkip: undefined,
  isAddressDone: false,
  isSkipped: false,
};

export default AddressSearch;
